<template>
  <div class="campaign-metrics">
    <!-- Header -->
    <div class="campaign-metrics__header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <b-icon
            icon="arrow-left-square-fill"
            class="back-arrow h3 mb-0 cursor-pointer"
            @click="$router.go(-1)"
          ></b-icon>
          <div class="ml-3">
            <h1 class="page-title">
              Reportes de Campañas : "{{ campaignData ? campaignData.nombre : '' }}"
            </h1>
          </div>
        </div>

        <!-- Filtros -->
        <div class="d-flex" v-if="!loading && metricas">
          <div class="filter-group">
            <label class="filter-label">Variante</label>
            <b-form-select
              v-model="varianteSeleccionada"
              :options="opcionesVariantes"
              class="filter-select"
            ></b-form-select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Fecha</label>
            <b-form-select
              v-model="rangoFecha"
              :options="opcionesRangoFecha"
              class="filter-select"
            ></b-form-select>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2 text-muted">Cargando métricas...</p>
    </div>

    <!-- Error -->
    <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
      {{ error }}
    </b-alert>

    <!-- Contenido de métricas -->
    <div v-if="!loading && metricas" class="metrics-content">
      <!-- Cards de métricas -->
      <div class="metrics-cards">
        <div class="metric-card">
          <div class="metric-label">Total Enviados</div>
          <div class="metric-value">{{ metricas.metricas_generales.total_enviados | formatNumber }}</div>
          <div class="metric-percentage"><span>100%</span></div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Entregados</div>
          <div class="metric-value">{{ metricas.metricas_generales.total_entregados | formatNumber }}</div>
          <div class="metric-percentage"> <span>{{ metricas.metricas_generales.tasa_entrega }}% </span> Tasa de Entrega</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Abiertos</div>
          <div class="metric-value">{{ metricas.metricas_generales.total_abiertos | formatNumber }}</div>
          <div class="metric-percentage"> <span>{{ metricas.metricas_generales.tasa_apertura }}% </span> Tasa de Apertura</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Clicks</div>
          <div class="metric-value">{{ metricas.metricas_generales.total_clicks | formatNumber }}</div>
          <div class="metric-percentage"> <span>{{ metricas.metricas_generales.tasa_clicks }}% </span> Tasa de Click</div>
        </div>

        <div class="metric-card">
          <div class="metric-label">Rebotados</div>
          <div class="metric-value">{{ metricas.metricas_generales.total_rebotados | formatNumber }}</div>
          <div class="metric-percentage"> <span>{{ metricas.metricas_generales.tasa_rebote }}% </span> Tasa de Rebote</div>
        </div>
      </div>

      <!-- Gráficos -->
      <div class="charts-row">
        <!-- Gráfico de Dona -->
        <div class="chart-card">
          <h3 class="chart-title">Distribución de Entrega</h3>
          <div class="chart-container-dona">
            <canvas ref="donutChart"></canvas>
          </div>
        </div>

        <!-- Gráfico de Líneas -->
        <div class="chart-card chart-card-large">
          <h3 class="chart-title">Evolución (Últimos 30 Días)</h3>
          <div class="chart-container-line">
            <canvas ref="lineChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Tabla de Variantes -->
      <div class="variants-table-card">
        <h3 class="chart-title">Comparativa de Variantes (A/B Testing)</h3>
        <b-table
          :items="metricas.metricas_por_variante || []"
          :fields="camposTablaVariantes"
          sort-by="variante_asunto"
          responsive
          class="variants-table"
        >
          <template #cell(variante_asunto)="data">
            <div class="variant-name table-bold">Variante {{ data.item.variante_numero }}: {{ data.value }}</div>
          </template>
          <template #cell(total_enviados)="data">
            <span class="table-bold">{{ data.value | formatNumber }}</span>
          </template>
          <template #cell(tasa_apertura)="data">
            <span>{{ data.value }}%</span>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Chart from 'chart.js/auto';

export default {
  name: 'CampaignMetrics',
  data() {
    return {
      campaignData: null,
      metricas: null,
      loading: false,
      error: null,
      varianteSeleccionada: 'todas',
      rangoFecha: '30',
      donutChart: null,
      lineChart: null
    };
  },
  computed: {
    campaignId() {
      return this.$route.params.id;
    },
    opcionesVariantes() {
      const opciones = [{ value: 'todas', text: 'Todas' }];
      if (this.metricas && this.metricas.metricas_por_variante) {
        this.metricas.metricas_por_variante.forEach((variante, index) => {
          opciones.push({
            value: index,
            text: `Variante ${variante.variante_numero}`
          });
        });
      }
      return opciones;
    },
    opcionesRangoFecha() {
      return [
        { value: '7', text: 'Últimos 7 Días' },
        { value: '30', text: 'Últimos 30 Días' },
        { value: '90', text: 'Últimos 90 Días' }
      ];
    },
    camposTablaVariantes() {
      return [
        { 
          key: 'variante_asunto', 
          label: 'Variante (Asunto)', 
          sortable: true
        },
        { 
          key: 'total_enviados', 
          label: 'Enviados', 
          sortable: true, 
          class: 'text-center'
        },
        { 
          key: 'tasa_apertura', 
          label: 'Apertura %', 
          sortable: true, 
          class: 'text-center'
        }
      ];
    }
  },
  filters: {
    formatNumber(value) {
      if (!value && value !== 0) return '0';
      return value.toLocaleString('es-CL');
    }
  },
  mounted() {
    this.cargarDatos();
  },
  beforeDestroy() {
    if (this.donutChart) this.donutChart.destroy();
    if (this.lineChart) this.lineChart.destroy();
  },
  methods: {
    ...mapActions('Campaign', ['fetchCampana', 'fetchMetricasDetalladas']),
    async cargarDatos() {
      this.loading = true;
      this.error = null;
      
      try {
        // Cargar campaña y métricas en paralelo
        const [campana, metricas] = await Promise.all([
          this.fetchCampana(this.campaignId),
          this.fetchMetricasDetalladas(this.campaignId)
        ]);
        
        this.campaignData = campana;
        this.metricas = metricas;
        
        // Renderizar gráficos después de que el DOM se actualice
        this.$nextTick(() => {
          this.renderDonutChart();
          this.renderLineChart();
        });
      } catch (err) {
        this.error = 'Error al cargar los datos de la campaña';
        console.error('Error:', err);
      } finally {
        this.loading = false;
      }
    },
    renderDonutChart() {
      if (!this.$refs.donutChart || !this.metricas || !this.metricas.metricas_generales) return;
      
      if (this.donutChart) this.donutChart.destroy();
      
      const ctx = this.$refs.donutChart.getContext('2d');
      const { metricas_generales } = this.metricas;
      
      this.donutChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Entregados', 'Rebotados'],
          datasets: [{
            data: [
              metricas_generales.tasa_entrega,
              metricas_generales.tasa_rebote
            ],
            backgroundColor: ['#34C38F', '#F46A6A'],
            borderWidth: 2,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '50%',
          plugins: {
            legend: {
              position: 'right',
              labels: {
                pointStyle: 'square',
                padding: 15,
                font: {
                  size: 12
                },
                generateLabels: (chart) => {
                  const { data } = chart;
                  return data.labels.map((label, i) => ({
                    text: `${label} ${data.datasets[0].data[i]}%`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    hidden: false,
                    index: i
                  }));
                }
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  return `${context.label}: ${context.parsed}%`;
                }
              }
            }
          }
        }
      });
    },
    renderLineChart() {
      if (!this.$refs.lineChart || !this.metricas || !this.metricas.evolucion_temporal) return;
      
      if (this.lineChart) this.lineChart.destroy();
      
      const ctx = this.$refs.lineChart.getContext('2d');
      const evolucion = this.metricas.evolucion_temporal || [];
      
      this.lineChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: evolucion.map(item => item.fecha),
          datasets: [
            {
              label: 'Enviados',
              data: evolucion.map(item => item.enviados),
              borderColor: '#34C38F',
              backgroundColor: '#34C38F',
              borderWidth: 2,
              tension: 0,
              fill: false,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#34C38F',
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            },
            {
              label: 'Abiertos',
              data: evolucion.map(item => item.abiertos),
              borderColor: '#F1B44C',
              backgroundColor: '#F1B44C',
              borderWidth: 2,
              tension: 0,
              fill: false,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#F1B44C',
              pointBorderColor: '#fff',
              pointBorderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              position: 'top',
              align: 'end',
              labels: {
                usePointStyle: false,
                pointStyle: 'square',
                padding: 15,
                font: {
                  size: 12
                }
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                title: (context) => {
                  const index = context[0].dataIndex;
                  return evolucion[index]?.fecha_detalle || context[0].label;
                },
                label: (context) => {
                  const label = context.dataset.label || '';
                  const value = context.parsed.y;
                  return `${label}: ${value.toLocaleString('es-CL')}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                font: {
                  size: 11
                }
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              }
            },
            x: {
              ticks: {
                font: {
                  size: 11
                },
                maxRotation: 45,
                minRotation: 0
              },
              grid: {
                display: false
              }
            }
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.campaign-metrics {
  padding: 24px;
  background: #F5F6FA;
  min-height: calc(100vh - 60px);
}

.campaign-metrics__header {
  margin-bottom: 24px;
}

::v-deep .back-arrow {
  color: #FECE2F;
  background-color: black;
  border-radius: 15px;
  cursor: pointer;
}

.cursor-pointer {
  cursor: pointer;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 0;
}

.page-subtitle {
  font-size: 14px;
  color: #666;
  margin: 4px 0 0 0;
}

.filter-group {
  display: flex;
  flex-direction: column;
  margin-left: 16px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #000;
  margin-bottom: 4px;
}

.filter-select {
  min-width: 180px;
  font-size: 14px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  padding: 6px 12px;
}

.metrics-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.metrics-cards {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.metric-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  height: 120px;
}

.metric-value {
  font-size: 28px;
  font-weight: 700;
  color: #23201C;
  margin-bottom: 8px;
}

.metric-label {
  font-size: 15px;
  color: rgba(82, 82, 82, 1);
  margin-bottom: 8px;
  font-weight: 700;
}

.metric-percentage {
  font-size: 15px;
  color: #525252;
}

.metric-percentage span {
  font-weight: 700;
  font-size: 15px;
  color: #000;
}

.metric-percentage.success {
  color: #34C38F;
}

.metric-percentage.info {
  color: #50A5F1;
}

.metric-percentage.danger {
  color: #F46A6A;
}

.charts-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
}

.chart-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  grid-column: span 2;
  height: 200px;
}

.chart-card-large {
  grid-column: span 3;
  height: 200px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin-bottom: 12px;
}

.chart-container-dona {
  position: relative;
  height: calc(200px - 48px - 32px);
}

.chart-container-line {
  position: relative;
  height: calc(200px - 48px - 32px);
}

.variants-table-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.variants-table {
  margin-top: 16px;
  margin-bottom: 0;
}

::v-deep .variants-table thead th {
  background-color: rgba(221, 221, 225, 1) !important;
  color: rgba(82, 82, 82, 1) !important;
  font-weight: 600;
  font-size: 14px;
  border: none !important;
  padding: 12px 16px !important;
  text-align: left !important;
}

::v-deep .variants-table tbody td {
  padding: 12px 16px !important;
  vertical-align: middle !important;
  font-size: 14px;
  color: #374151;
  border-top: 1px solid #E5E7EB !important;
  text-align: left !important;
}

::v-deep .variants-table tbody tr {
  background-color: #FFFFFF !important;
}

::v-deep .variants-table tbody tr:hover {
  background-color: #F9FAFB !important;
}

.variant-name {
  font-weight: 500;
  color: #111827;
}

.percentage-badge {
  display: inline-block;
  padding: 4px 12px;
  background-color: #F3F4F6;
  border-radius: 6px;
  font-weight: 500;
  color: #374151;
}

@media (max-width: 1400px) {
  .metrics-cards {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .charts-row {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .chart-card {
    grid-column: span 1;
  }
  
  .chart-card-large {
    grid-column: span 2;
  }
}

@media (max-width: 992px) {
  .metrics-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .charts-row {
    grid-template-columns: 1fr;
  }
  
  .chart-card,
  .chart-card-large {
    grid-column: span 1;
  }
}

@media (max-width: 576px) {
  .metrics-cards {
    grid-template-columns: 1fr;
  }
  
  .campaign-metrics__header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .filter-group {
    margin-left: 0;
    margin-top: 12px;
  }
}

.table-bold {
  font-weight: 600;
  color: rgba(82, 82, 82, 1);
}
</style>
